import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clinicalSessionApi } from '../../services/clinicalSessionApi';
import { ClinicalSessionStatus } from '../../dtos/ClinicalSessions/index';

export const clinicalSessionKeys = {
  all: ['clinicalSessions'] as const,
  lists: () => [...clinicalSessionKeys.all, 'list'] as const,
  list: (filters?: { pageNumber?: number; pageSize?: number; status?: ClinicalSessionStatus | null }) =>
    [...clinicalSessionKeys.lists(), filters] as const,
  byUser: (userId: string) => [...clinicalSessionKeys.all, 'byUser', userId] as const,
  byPerson: (personId: string) => [...clinicalSessionKeys.all, 'byPerson', personId] as const,
  details: () => [...clinicalSessionKeys.all, 'detail'] as const,
  detail: (id: string) => [...clinicalSessionKeys.details(), id] as const,
};

export function useGetClinicalSession(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: clinicalSessionKeys.detail(id),
    queryFn: async () => {
      const response = await clinicalSessionApi.getById(id);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch clinical session'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!id,
  });
}

export function useGetClinicalSessions() {
  return useQuery({
    queryKey: clinicalSessionKeys.lists(),
    queryFn: async () => {
      const response = await clinicalSessionApi.getList();
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch clinical sessions'}`);
      }
      return response;
    },
  });
}

export function useGetClinicalSessionsByUserId(userId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: clinicalSessionKeys.byUser(userId),
    queryFn: async () => {
      const response = await clinicalSessionApi.getByUserId(userId);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch clinical sessions'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!userId,
  });
}

export function useGetClinicalSessionsByPersonId(personId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: clinicalSessionKeys.byPerson(personId),
    queryFn: async () => {
      const response = await clinicalSessionApi.getByPersonId(personId);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch clinical sessions'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!personId,
  });
}

export function useGetClinicalSessionsPaged(
  pageNumber: number = 1,
  pageSize: number = 10,
  status?: ClinicalSessionStatus | null
) {
  return useQuery({
    queryKey: clinicalSessionKeys.list({ pageNumber, pageSize, status }),
    queryFn: async () => {
      const response = await clinicalSessionApi.getPagedList(pageNumber, pageSize, status);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch clinical sessions'}`);
      }
      return response;
    },
  });
}
